import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUser } from '@/contexts/UserContext';
import { User, Mail, Lock, Key, UserPlus } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();

const SignupPage = () => {
  const navigate = useNavigate();
  const { user, login } = useUser();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    coachName: '',
    coachIdKey: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  // Check for auth message from callback and auto-redirect
  useEffect(() => {
    // Check for message from auth callback
    const authMessage = sessionStorage.getItem('auth_message');
    if (authMessage) {
      setInfoMessage(authMessage);
      sessionStorage.removeItem('auth_message');
    }
    
    // Auto-redirect if already logged in
    if (user) {
      if (user.role === 'master_admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (user.role === 'coach') {
        navigate('/coach/dashboard', { replace: true });
      } else {
        navigate('/client/hub', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // Client requires coach info
    if (!formData.coachName || !formData.coachIdKey) {
      setError('Please provide your coach name and ID key');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: 'client',
        coach_name: formData.coachName,
        coach_id_key: formData.coachIdKey
      };

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      login(data.user, data.access_token);
      navigate('/client/hub');
    } catch (err) {
      setError(err.message || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory-mist flex items-center justify-center p-4 pb-20">
      <div className="max-w-md w-full">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-obsidian-night mb-2">GNTL STRNGTH</h1>
          <p className="text-shadow-pine font-body text-sm sm:text-base">Begin your presence calibration</p>
        </div>

        {/* Info message from auth callback */}
        {infoMessage && (
          <div className="mb-4 p-3 bg-ivory-mist border border-shadow-pine/30 rounded-lg text-center">
            <p className="text-obsidian-night font-body text-sm">{infoMessage}</p>
          </div>
        )}

        <Card className="bg-white border-burnt-terra/20">
          <CardHeader className="pb-4 sm:pb-6">
            <CardTitle className="font-display text-obsidian-night text-center text-lg sm:text-xl">
              Client Registration
            </CardTitle>
            <CardDescription className="text-shadow-pine font-body text-center text-sm">
              Enter your details and coach information to get started
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-obsidian-night font-body text-sm">Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-obsidian-night font-body text-sm">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-obsidian-night font-body text-sm">Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-obsidian-night font-body text-sm">Confirm Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                  />
                </div>
              </div>

              <div className="border-t border-shadow-pine/20 pt-4 mt-4">
                <p className="text-sm text-shadow-pine/70 font-body mb-3">
                  Enter your coach&apos;s information to link your account
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="coachName" className="text-obsidian-night font-body text-sm">Coach Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="coachName"
                    type="text"
                    placeholder="Enter your coach's name"
                    value={formData.coachName}
                    onChange={(e) => setFormData({ ...formData, coachName: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="coachIdKey" className="text-obsidian-night font-body text-sm">Coach ID Key *</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="coachIdKey"
                    type="text"
                    placeholder="Enter your coach's ID key"
                    value={formData.coachIdKey}
                    onChange={(e) => setFormData({ ...formData, coachIdKey: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm font-body">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-burnt-terra text-ivory-mist hover:bg-[#A54A2F] font-semibold"
              >
                {loading ? 'Creating account...' : 'Create Account'}
                <UserPlus className="w-4 h-4 ml-2" />
              </Button>
            </form>

            <p className="text-center text-sm text-shadow-pine/60 font-body mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-burnt-terra hover:underline font-semibold">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
      <BrandMark />
    </div>
  );
};

export default SignupPage;
